/**
 * Copyright (c) 2020-2021 Perficient, Inc.
 * Unauthorized copying or distribution of this file prohibited without the express permission of Perficient.
 */

import React from "react";
import { MenuItem, InputLabel, FormControl, Select } from "@material-ui/core";
import FormTextField from "../../../components/FormTextField/FormTextField";
import { WEB_CHAT_TOOL_TIPS } from "../../../utility/toolTipMessages";
import SurveyFollowUpQuestionCard from "./SurveyFollowUpQuestionCard";
import { SURVEY_QUESTION_TYPE } from "../../../utility/constants";
import { testForRangeOverlap } from "../../../utility";
import { ActionButtons } from "../../../components/Common";
const newFollowUpQuestion = () => ({
	question: "",
	range: [1, 5],
	binaryAnswer: 1,
});

/**
 * This component is used on the Chat configuration page to render the various neccesary fields for editing the web chat widgets configuration.
 * @param {Object} selectedConfiguration The currently selected chat configuration to edit.
 * @param {(Object) => void} setSelectedConfiguration Callback method to set the currently selected chat configuration to edit.
 * @param {Array} contactFlows A list of all the contact flows found in AWS Connect.
 * @param {() => void} setFormEdited Callback method to set the form as edited.
 * @param {boolean} formEdited A boolean value of whether or not te values on this form have been edited.
 */
const SurveyQuestionCard = ({
	surveyQuestion,
	setSurveyQuestion,
	removeSurveyQuestion,
	moveQuestionUp,
	moveQuestionDown,
	surveyQuestionsLength,
	formEdited,
	setFormEdited,
}) => {
	const setQuestionValue = (valueName, value) => {
		const newSurveyQuestion = { ...surveyQuestion };
		newSurveyQuestion[valueName] = value;
		setSurveyQuestion(newSurveyQuestion);
	};
	return (
		<div className="d-flex my-4">
			<div className="w-100">
				<div
					className="d-flex justify-content-between align-items-center rounded p-1"
					style={{ background: "#eeeeee" }}
				>
					<h4 className="m-0">Question {surveyQuestion.index + 1}</h4>
					<ActionButtons
						showCancel
						showRemove
						cancelBtnClass="text-dark border-secondary"
						removeBtnClass="text-dark border-secondary"
						onCancel={moveQuestionDown}
						onRemove={moveQuestionUp}
						disableCancel={surveyQuestion.index === surveyQuestionsLength - 1}
						disableRemove={surveyQuestion.index === 0}
						cancelLabel="Move Down"
						removeLabel="Move Up"
					/>
				</div>
				<div className="mx-auto my-2" style={{ width: "90%" }}>
					<div style={{ marginBottom: "20px" }}>
						<FormControl style={{ width: "200px" }}>
							<InputLabel id="question-type-select-helper-label">Answer Type:</InputLabel>
							<Select
								labelId="question-type-select-helper-label"
								id="question-type-select-helper"
								value={surveyQuestion.questionType}
								onChange={(event) => {
									setQuestionValue("questionType", event.target.value);
								}}
							>
								<MenuItem value={SURVEY_QUESTION_TYPE.SCALE}>Scale</MenuItem>
								<MenuItem value={SURVEY_QUESTION_TYPE.BINARY}>Yes or No</MenuItem>
								<MenuItem value={SURVEY_QUESTION_TYPE.TEXT}>Text</MenuItem>
							</Select>
						</FormControl>
					</div>
					<FormTextField
						styleObject={{ width: "100%" }}
						label={"Question"}
						formEdited={formEdited}
						setEdited={setFormEdited}
						required
						errorMessage={"Should contain a value."}
						startingValue={surveyQuestion.question || ""}
						setConfigValue={setQuestionValue}
						valueName={"question"}
						toolTipMessage={WEB_CHAT_TOOL_TIPS.SURVEY_QUESTION}
					/>
					{surveyQuestion.questionType !== SURVEY_QUESTION_TYPE.TEXT && (
						<div className="w-90 mt-3">
							<h6 className="rounded p-1" style={{ background: "#eeeeee" }}>
								Follow Up Questions
							</h6>
							<div className="mb-2">
								{surveyQuestion.followUpQuestions.map((followUp, index) => {
									return (
										<SurveyFollowUpQuestionCard
											key={`surveyQuestion${surveyQuestion.index}FollowUpQuestion${index}`}
											followUpQuestion={followUp}
											questionType={surveyQuestion.questionType}
											error={
												surveyQuestion.followUpQuestions.find(
													(element, elementIndex) =>
														index !== elementIndex &&
														(surveyQuestion.questionType === SURVEY_QUESTION_TYPE.SCALE
															? testForRangeOverlap(element.range, followUp.range)
															: element.binaryAnswer === followUp.binaryAnswer),
												) !== undefined
											}
											setFollowUpQuestion={(newFollowUp) => {
												const newFollowUpQuestions = [...surveyQuestion.followUpQuestions];
												newFollowUpQuestions[index] = newFollowUp;
												setQuestionValue("followUpQuestions", newFollowUpQuestions);
											}}
											removeFollowUpQuestion={() => {
												const newFollowUpQuestions = [...surveyQuestion.followUpQuestions];
												newFollowUpQuestions.splice(index, 1);
												setQuestionValue("followUpQuestions", newFollowUpQuestions);
											}}
											formEdited={formEdited}
											setFormEdited={setFormEdited}
										/>
									);
								})}
							</div>
							<ActionButtons
								className="justify-content-center"
								showCancel={surveyQuestion.followUpQuestions.length < 2}
								onCancel={() => {
									const newFollowUpQuestions = [...surveyQuestion.followUpQuestions];
									newFollowUpQuestions.push(newFollowUpQuestion());
									setQuestionValue("followUpQuestions", newFollowUpQuestions);
								}}
								cancelLabel={`Add ${surveyQuestion.followUpQuestions.length === 1 ? "Second" : ""} Follow Up Question`}
							/>
						</div>
					)}
				</div>
				<ActionButtons
					showCancel
					onCancel={removeSurveyQuestion}
					className="float-right"
					cancelLabel={`Delete Question ${surveyQuestion.index + 1}`}
				/>
			</div>
		</div>
	);
};

export default SurveyQuestionCard;
